import React, { useState } from 'react';
import {
  Badge, Button, Empty, Form, InputNumber, Modal, Popconfirm, Select, Space, Switch, Table, Typography,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { formatDiscount } from '../serviceTypes';

const { Text } = Typography;

const REGION_FIELDS = [
  { key: 'cn', label: '国内（CNY）' },
  { key: 'os', label: '海外（USD）' },
];

const normalizePolicy = (p) => ({
  isDeductBasePrice: !!p?.isDeductBasePrice,
  discountRatio: p?.isDeductBasePrice ? Number(p?.discountRatio ?? 100) : 100,
});

// 服务类型底价策略表（国内 / 海外同表配置）
// overrides: { [serviceId]: { cn: 策略, os: 策略 } }
// fallback:  未添加时展示的上一层默认策略 { cn, os }
const ServiceTypePolicyTable = ({
  serviceTypes, overrides = {}, fallback, isPlatform, onChange,
  title = '服务类型策略', layerLabel = '企业默认策略', addText = '添加服务类型',
}) => {
  const [visible, setVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  // 服务分类 → 服务类型 分组选项
  const categoryOptions = (() => {
    const groups = [];
    serviceTypes.forEach((s) => {
      const categoryName = s.categoryName || '未分类';
      const last = groups[groups.length - 1];
      if (last && last.label === categoryName) last.items.push(s);
      else groups.push({ label: categoryName, items: [s] });
    });
    return groups.map((g) => ({
      label: g.label,
      options: g.items.map((s) => ({ value: s.serviceId, label: `${s.serviceName}（${s.serviceId}）` })),
    }));
  })();

  const availableOptions = categoryOptions
    .map((g) => ({ ...g, options: g.options.filter((o) => !overrides[o.value]) }))
    .filter((g) => g.options.length > 0);

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      cn: { isDeductBasePrice: fallback?.cn?.isDeductBasePrice ?? true, discountRatio: fallback?.cn?.discountRatio ?? 100 },
      os: { isDeductBasePrice: fallback?.os?.isDeductBasePrice ?? true, discountRatio: fallback?.os?.discountRatio ?? 100 },
    });
    setVisible(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      cn: normalizePolicy(record.cn),
      os: normalizePolicy(record.os),
    });
    setVisible(true);
  };

  const save = async () => {
    try {
      const v = await form.validateFields();
      const serviceId = editing ? editing.serviceId : v.serviceId;
      if (!serviceId) return;
      onChange(serviceId, {
        cn: normalizePolicy(v.cn),
        os: normalizePolicy(v.os),
      });
      setVisible(false);
      setEditing(null);
    } catch (e) { /* 校验失败 */ }
  };

  const rows = Object.entries(overrides).map(([serviceId, policy]) => {
    const st = serviceTypes.find((s) => s.serviceId === serviceId);
    return {
      serviceId,
      serviceName: st ? st.serviceName : serviceId,
      categoryName: st ? st.categoryName || '未分类' : '未分类',
      cn: policy?.cn || fallback?.cn,
      os: policy?.os || fallback?.os,
    };
  });

  const policyCell = (policy) => (
    <div>
      <Badge status={policy?.isDeductBasePrice ? 'success' : 'default'} />
      <Text style={{ marginLeft: 6, color: policy?.isDeductBasePrice ? '#52c41a' : '#999' }}>
        {policy?.isDeductBasePrice ? `扣除 / ${formatDiscount(policy.discountRatio)}` : '不扣除'}
      </Text>
    </div>
  );

  const columns = [
    { title: '服务分类', dataIndex: 'categoryName', key: 'category', width: 110 },
    {
      title: '服务类型', key: 'service', width: 190,
      render: (_, r) => (
        <div style={{ paddingLeft: 12 }}>
          <div style={{ fontWeight: 500 }}>{r.serviceName}</div>
          <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>{r.serviceId}</Text>
        </div>
      ),
    },
    { title: '国内（CNY）', key: 'cn', width: 180, render: (_, r) => policyCell(r.cn) },
    { title: '海外（USD）', key: 'os', width: 180, render: (_, r) => policyCell(r.os) },
    {
      title: '操作', key: 'action', width: 150,
      render: (_, r) => {
        if (!isPlatform) return <Text type="secondary">只读</Text>;
        return (
          <Space size={0}>
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>编辑</Button>
            <Popconfirm
              title={`删除 ${r.serviceName} 的单独配置？`}
              description={`删除后该服务类型按${layerLabel}生效`}
              okText="确认删除" cancelText="取消" okButtonProps={{ danger: true }}
              onConfirm={() => onChange(r.serviceId, null)}
            >
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <Text strong style={{ fontSize: 14 }}>{title}</Text>
        {isPlatform && (
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openAdd}>{addText}</Button>
        )}
      </div>

      {rows.length > 0 ? (
        <>
          <Table
            rowKey="serviceId"
            columns={columns}
            dataSource={rows}
            pagination={false}
            size="small"
            bordered
            scroll={{ x: 810 }}
          />
          <Text type="secondary" style={{ fontSize: 12, display: 'inline-block', marginTop: 8 }}>
            未添加的服务类型按{layerLabel}生效；新增服务类型不会自动进入列表，可按需添加。
          </Text>
        </>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={`未单独配置服务类型，全部服务类型按${layerLabel}生效`}
          style={{ margin: '12px 0' }}
        />
      )}

      <Modal
        title={editing ? `编辑服务类型策略 - ${editing.serviceName}` : '添加服务类型策略'}
        open={visible}
        onOk={save}
        onCancel={() => { setVisible(false); setEditing(null); }}
        okText="保存" cancelText="取消" destroyOnClose width={620}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          {!editing && (
            <Form.Item
              name="serviceId" label="服务类型"
              rules={[{ required: true, message: '请选择服务类型' }]}
              extra="已单独配置的服务类型不再出现在选项中"
            >
              <Select placeholder="请选择服务类型" showSearch optionFilterProp="label" options={availableOptions} />
            </Form.Item>
          )}
          {editing && (
            <Form.Item label="服务类型">
              <Text strong>{editing.serviceName}</Text>
              <Text type="secondary" style={{ marginLeft: 8, fontFamily: 'monospace' }}>{editing.serviceId}</Text>
            </Form.Item>
          )}

          {REGION_FIELDS.map((r) => (
            <div
              key={r.key}
              style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: '12px 16px', marginBottom: 12 }}
            >
              <div style={{ fontWeight: 600, marginBottom: 8 }}>{r.label}</div>
              <Form.Item name={[r.key, 'isDeductBasePrice']} label="是否扣除套餐底价" valuePropName="checked" style={{ marginBottom: 8 }}>
                <Switch checkedChildren="扣除" unCheckedChildren="不扣" />
              </Form.Item>
              <Form.Item noStyle shouldUpdate={(p, c) => p?.[r.key]?.isDeductBasePrice !== c?.[r.key]?.isDeductBasePrice}>
                {({ getFieldValue }) => (getFieldValue([r.key, 'isDeductBasePrice']) ? (
                  <Form.Item
                    name={[r.key, 'discountRatio']} label="扣除折扣比例"
                    rules={[{ required: true, message: '请输入扣除折扣比例' }]}
                    style={{ marginBottom: 0 }}
                    extra="100% 表示不打折，0% 等同不扣除"
                  >
                    <InputNumber min={0} max={100} precision={2} style={{ width: '100%' }} addonAfter="%" />
                  </Form.Item>
                ) : null)}
              </Form.Item>
            </div>
          ))}
        </Form>
      </Modal>
    </div>
  );
};

export default ServiceTypePolicyTable;
